import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from 'lift-ui';
import { Play, RefreshCw, Trash2 } from 'lucide-react';
import { useDiscardJob, useDispatchJob, useJob, useJobsAccess, useRetryJob } from '../hooks';
import { formatDuration, formatTimestamp } from '../utils';
import { JobStatusBadge } from './JobStatusBadge';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <div className="text-sm break-words">{children}</div>
  </div>
);

interface JobDetailSheetProps {
  jobId: string | null;
  onClose: () => void;
}

export const JobDetailSheet = ({ jobId, onClose }: JobDetailSheetProps) => {
  const { canEdit, canManage } = useJobsAccess();
  const { data: job, isLoading, isError } = useJob(jobId);
  const retryJob = useRetryJob();
  const dispatchJob = useDispatchJob();
  const discardJob = useDiscardJob();

  const isMutating = retryJob.isPending || dispatchJob.isPending || discardJob.isPending;
  const canDispatch = job ? ['scheduled', 'blocked'].includes(job.status ?? '') : false;

  return (
    <Sheet open={Boolean(jobId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{job?.job_class_name ?? 'Job'}</SheetTitle>
          <SheetDescription className="font-mono text-xs">{jobId}</SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="mt-6 flex flex-col gap-3">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {isError && (
          <p className="mt-6 text-sm text-destructive">
            This job could not be loaded. It may have been retried or discarded already.
          </p>
        )}

        {job && (
          <div className="mt-6 flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Status">
                <JobStatusBadge status={job.status} />
              </Field>
              <Field label="Queue">
                <Badge variant="greyLight">{job.queue_name}</Badge>
              </Field>
              <Field label="Enqueued">{formatTimestamp(job.enqueued_at)}</Field>
              <Field label="Scheduled">{formatTimestamp(job.scheduled_at)}</Field>
              <Field label="Started">{formatTimestamp(job.started_at)}</Field>
              <Field label="Finished">{formatTimestamp(job.finished_at)}</Field>
              <Field label="Attempts">{job.executions ?? '—'}</Field>
              <Field label="Duration">{formatDuration(job.duration_seconds)}</Field>
              {job.blocked_by && <Field label="Blocked by">{job.blocked_by}</Field>}
              {job.worker_id !== null && <Field label="Worker">{job.worker_id}</Field>}
            </div>

            <Field label="Arguments">
              {job.arguments.length ? (
                <pre className="rounded-md bg-muted p-3 text-xs whitespace-pre-wrap">
                  {job.arguments.join('\n')}
                </pre>
              ) : (
                <span className="text-muted-foreground">No arguments</span>
              )}
            </Field>

            {job.error && (
              <Field label="Error">
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <p className="text-sm font-medium text-destructive">
                    {job.error.error_class}: {job.error.message}
                  </p>
                  {job.error.backtrace?.length ? (
                    <pre className="mt-2 max-h-64 overflow-auto text-xs whitespace-pre-wrap text-muted-foreground">
                      {job.error.backtrace.join('\n')}
                    </pre>
                  ) : null}
                </div>
              </Field>
            )}

            {(canEdit || canManage) && (
              <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                {canEdit && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={job.status !== 'failed' || isMutating}
                      onClick={() => retryJob.mutate(job.job_id, { onSuccess: onClose })}
                    >
                      <RefreshCw size={14} />
                      Retry
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!canDispatch || isMutating}
                      onClick={() => dispatchJob.mutate(job.job_id, { onSuccess: onClose })}
                    >
                      <Play size={14} />
                      Dispatch
                    </Button>
                  </>
                )}
                {canManage && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => discardJob.mutate(job.job_id, { onSuccess: onClose })}
                  >
                    <Trash2 size={14} />
                    Discard
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default JobDetailSheet;
